import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import LogoImage from "../assets/Logo.jpg";
import AnchoringBg from "../assets/anchoring bg.png";

// Enhanced animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8) rotate(-5deg);
    filter: blur(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
    filter: blur(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
    filter: blur(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;



const morphing = keyframes`
  0%, 100% {
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    transform: rotate(0deg) scale(1);
  }
  25% {
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
    transform: rotate(90deg) scale(1.1);
  }
  50% {
    border-radius: 70% 30% 60% 40% / 30% 50% 40% 70%;
    transform: rotate(180deg) scale(0.9);
  }
  75% {
    border-radius: 40% 70% 30% 60% / 70% 40% 60% 30%;
    transform: rotate(270deg) scale(1.05);
  }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const HomeContainer = styled.div`
  background: linear-gradient(
    135deg,
    #f8fcff 0%,
    #e8f4fd 15%,
    #d1e9fc 35%,
    #b8ddfa 55%,
    #9fd1f8 75%,
    #87c5f6 90%,
    #6bb6ff 100%
  );
  background-size: 400% 400%;
  animation: ${gradientShift} 25s ease infinite;
  min-height: 60vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 1rem 0;

  @media (max-width: 1024px) {
    min-height: 50vh;
    padding: 1rem 0;
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 2rem 0;
    align-items: center;
  }

  @media (max-width: 480px) {
    min-height: auto;
    padding: 1.5rem 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(ellipse at 20% 30%, rgba(0, 191, 255, 0.12) 0%, transparent 60%),
      radial-gradient(ellipse at 80% 20%, rgba(30, 144, 255, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 70% 80%, rgba(0, 123, 255, 0.10) 0%, transparent 55%),
      radial-gradient(ellipse at 30% 85%, rgba(135, 197, 246, 0.15) 0%, transparent 65%),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.25) 0%, transparent 80%);
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: -30%;
    left: -30%;
    width: 160%;
    height: 160%;
    background: conic-gradient(
      from 45deg,
      transparent 60%,
      rgba(255, 255, 255, 0.05) 75%,
      rgba(0, 191, 255, 0.02) 85%,
      transparent 95%
    );
    animation: ${gradientShift} 35s linear infinite;
    pointer-events: none;
    z-index: 1;
  }
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  width: 100%;
  z-index: 2;
  position: relative;
  min-height: 50vh;

  @media (max-width: 1200px) {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 1.5rem;
    padding: 1rem;
    max-width: 1000px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1.2fr 0.8fr;
    padding: 0.8rem;
    gap: 0.8rem;
    align-items: center;
    max-width: 100%;
    min-height: auto;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1.3fr 0.7fr;
    gap: 0.5rem;
    padding: 0.5rem;
    align-items: center;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  z-index: 3;
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 1.8rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 15px 40px rgba(0, 191, 255, 0.1),
    0 6px 20px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  
  @media (min-width: 1201px) {
    padding-right: 1.8rem;
  }

  @media (max-width: 1200px) {
    text-align: left;
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    text-align: left;
    border-radius: 16px;
    margin: 0;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    border-radius: 14px;
    margin: 0;
    text-align: left;
  }
`;

const TrustBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.85)
  );
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #0066cc;
  font-weight: 700;
  box-shadow: 
    0 8px 32px rgba(0, 191, 255, 0.25),
    0 4px 16px rgba(0, 191, 255, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 191, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 0.6rem;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2300bfff'%3E%3Cpath d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E") no-repeat center;
    background-size: contain;
    animation: ${sparkle} 2s ease infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 191, 255, 0.15),
      transparent
    );
    transition: left 0.8s ease;
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 
      0 10px 30px rgba(0, 191, 255, 0.3),
      0 5px 15px rgba(0, 191, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: #00bfff;

    &::after {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.4rem 0.8rem;
    margin-bottom: 0.8rem;
    border-radius: 25px;
    justify-content: flex-start;
    max-width: 250px;
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.4rem 0.8rem;
    margin-bottom: 0.6rem;
    max-width: 250px;
    
    &::before {
      font-size: 0.9rem;
      margin-right: 0.4rem;
    }
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 0.8rem;
  animation: ${fadeInScale} 1.2s ease-out 0.3s both;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: 1200px) {
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.6rem;
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.6rem;
  }

  .main-logo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    box-shadow: 
      0 12px 30px rgba(0, 191, 255, 0.2),
      0 6px 15px rgba(0, 191, 255, 0.12),
      inset 0 2px 6px rgba(255, 255, 255, 0.4);
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.9);
    background: white;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 3;
    animation: none;

    &:hover {
      transform: scale(1.05) rotate(3deg);
      box-shadow: 
        0 15px 35px rgba(0, 191, 255, 0.25),
        0 8px 18px rgba(0, 191, 255, 0.15),
        inset 0 2px 8px rgba(255, 255, 255, 0.5);
    }

    @media (max-width: 768px) {
      width: 60px;
      height: 60px;
      border: 2px solid rgba(255, 255, 255, 0.9);
      
      &:hover {
        transform: scale(1.03) rotate(2deg);
      }
    }

    @media (max-width: 480px) {
      width: 50px;
      height: 50px;
      border: 2px solid rgba(255, 255, 255, 0.9);
      
      &:hover {
        transform: scale(1.02) rotate(1deg);
      }
    }
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    z-index: 1;
    animation: none;
  }

  &::before {
    top: -6%;
    right: -6%;
    width: 25px;
    height: 25px;
    background: linear-gradient(135deg, #00bfff, #1e90ff);
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.4);

    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
      top: -4%;
      right: -4%;
    }

    @media (max-width: 480px) {
      width: 12px;
      height: 12px;
      top: -2%;
      right: -2%;
    }
  }

  &::after {
    bottom: -2%;
    left: -10%;
    width: 18px;
    height: 18px;
    background: linear-gradient(135deg, #ff6b35, #ff8a50);
    box-shadow: 0 0 12px rgba(255, 107, 53, 0.3);

    @media (max-width: 768px) {
      width: 12px;
      height: 12px;
      bottom: -1%;
      left: -6%;
    }

    @media (max-width: 480px) {
      width: 10px;
      height: 10px;
      bottom: -1%;
      left: -4%;
    }
  }
`;

const MainHeading = styled.h1`
  font-size: 2.4rem;
  font-weight: 900;
  color: #1a1a1a;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  animation: ${fadeInUp} 1.2s ease-out 0.5s both;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: left;
  letter-spacing: -0.02em;

  @media (max-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1rem;
    line-height: 1.25;
    text-align: left;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
    line-height: 1.25;
    margin-bottom: 0.8rem;
  }

  .highlight {
    background: linear-gradient(
      135deg,
      #00bfff 0%,
      #1e90ff 25%,
      #0066cc 50%,
      #004499 75%,
      #00bfff 100%
    );
    background-size: 300% 300%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
    font-weight: 900;
    position: relative;
    animation: 
      ${slideInRight} 1.2s ease-out 0.8s both,
      ${gradientShift} 5s ease infinite;
    letter-spacing: -0.02em;
    margin-top: 0.2rem;

    &::after {
      content: "";
      position: absolute;
      bottom: -6px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, #00bfff, #1e90ff);
      border-radius: 2px;
      animation: expandWidth 1.2s ease-out 1.8s both;
      box-shadow: 0 2px 8px rgba(0, 191, 255, 0.3);

      @media (max-width: 768px) {
        height: 2px;
        bottom: -4px;
      }

      @media (max-width: 480px) {
        height: 1px;
        bottom: -3px;
      }
    }
  }

  @keyframes expandWidth {
    to { width: 100%; }
  }
`;

const SubHeading = styled.p`
  font-size: 1.15rem;
  color: #2d3748;
  margin-bottom: 2rem;
  max-width: 600px;
  line-height: 1.6;
  animation: ${fadeInUp} 1.2s ease-out 0.7s both;
  font-weight: 500;
  text-align: left;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1.2rem;
    max-width: 100%;
    line-height: 1.5;
    text-align: left;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.4;
    margin-bottom: 1rem;
    padding: 0 0.5rem;
  }

  .tech-highlight, .natural-highlight {
    font-weight: 700;
    position: relative;
    padding: 1px 3px;
    border-radius: 3px;
    transition: all 0.3s ease;
  }

  .tech-highlight {
    color: #0066cc;
    background: linear-gradient(135deg, transparent, rgba(0, 191, 255, 0.1));
    
    &:hover {
      background: rgba(0, 191, 255, 0.15);
      transform: scale(1.02);
      box-shadow: 0 2px 6px rgba(0, 191, 255, 0.2);
    }
  }

  .natural-highlight {
    color: #2d5a27;
    background: linear-gradient(135deg, transparent, rgba(76, 175, 80, 0.1));
    
    &:hover {
      background: rgba(76, 175, 80, 0.15);
      transform: scale(1.02);
      box-shadow: 0 2px 6px rgba(76, 175, 80, 0.2);
    }
  }
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
  animation: ${slideInLeft} 1.2s ease-out 0.4s both;

  @media (max-width: 1200px) {
    animation: ${fadeInScale} 1.2s ease-out 0.6s both;
    justify-content: center;
  }

  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    margin-top: 0;
  }

  @media (max-width: 480px) {
    margin-top: 0;
    justify-content: center;
  }

  .hero-image {
    width: 100%;
    max-width: 320px;
    height: auto;
    border-radius: 20px;
    position: relative;
    z-index: 3;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 15px 30px rgba(0, 191, 255, 0.15))
            drop-shadow(0 6px 15px rgba(0, 0, 0, 0.1));
    
    /* Create a subtle overlay effect */
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(0, 191, 255, 0.05) 0%,
        transparent 30%,
        transparent 70%,
        rgba(255, 255, 255, 0.1) 100%
      );
      border-radius: 24px;
      pointer-events: none;
      z-index: 1;
    }

    &:hover {
      transform: scale(1.02) translateY(-5px);
      filter: drop-shadow(0 25px 50px rgba(0, 191, 255, 0.2))
              drop-shadow(0 10px 25px rgba(0, 0, 0, 0.15));
    }

    @media (max-width: 1200px) {
      max-width: 280px;
    }

    @media (max-width: 768px) {
      max-width: 160px;
      border-radius: 16px;
      
      &::before {
        border-radius: 16px;
      }
      
      &:hover {
        transform: scale(1.01) translateY(-2px);
      }
    }

    @media (max-width: 480px) {
      max-width: 140px;
      border-radius: 12px;
      
      &::before {
        border-radius: 12px;
      }
      
      &:hover {
        transform: scale(1.005) translateY(-1px);
      }
    }
  }

  /* Enhanced floating elements with better positioning */
  &::before {
    content: "";
    position: absolute;
    top: -20px;
    right: -20px;
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(255, 138, 80, 0.3));
    border-radius: 50%;
    filter: blur(2px);
    z-index: 1;
    animation: ${morphing} 15s ease-in-out infinite;

    @media (max-width: 768px) {
      width: 50px;
      height: 50px;
      top: -10px;
      right: -10px;
    }

    @media (max-width: 480px) {
      width: 35px;
      height: 35px;
      top: -8px;
      right: -8px;
    }
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: -25px;
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(102, 187, 106, 0.3));
    border-radius: 50%;
    filter: blur(1px);
    z-index: 1;
    animation: ${morphing} 18s ease-in-out infinite reverse;

    @media (max-width: 768px) {
      width: 40px;
      height: 40px;
      bottom: -8px;
      left: -15px;
    }

    @media (max-width: 480px) {
      width: 25px;
      height: 25px;
      bottom: -5px;
      left: -10px;
    }
  }
`;

// Lazy Loading Components
const ImagePlaceholder = styled.div`
  width: 100%;
  max-width: 380px;
  height: 300px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9),
    rgba(240, 248, 255, 0.8)
  );
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    max-width: 280px;
    height: 220px;
  }

  @media (max-width: 768px) {
    max-width: 280px;
    height: 220px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    max-width: 240px;
    height: 190px;
    border-radius: 12px;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 191, 255, 0.1);
  border-top: 3px solid #00bfff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    border-width: 2px;
  }
`;

const LazyImage = styled.img`
  width: 100%;
  max-width: 380px;
  height: auto;
  border-radius: 18px;
  box-shadow: 
    0 12px 40px rgba(0, 191, 255, 0.2),
    0 6px 20px rgba(0, 191, 255, 0.12);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 3;
  opacity: ${props => props.loaded ? 1 : 0};
  transform: ${props => props.loaded ? 'scale(1)' : 'scale(0.95)'};

  &:hover {
    transform: scale(1.02) rotate(0.5deg);
    box-shadow: 
      0 15px 45px rgba(0, 191, 255, 0.25),
      0 8px 25px rgba(0, 191, 255, 0.15);
  }

  @media (max-width: 1200px) {
    max-width: 280px;
  }

  @media (max-width: 768px) {
    max-width: 280px;
    border-radius: 15px;
    
    &:hover {
      transform: scale(1.01);
    }
  }

  @media (max-width: 480px) {
    max-width: 240px;
    border-radius: 12px;
    
    &:hover {
      transform: scale(1.005);
    }
  }
`;

const FloatingOrbs = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;

  .orb {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(0, 191, 255, 0.15), rgba(30, 144, 255, 0.25));
    animation: ${morphing} 12s ease-in-out infinite;
    filter: blur(1px);

    &:nth-child(1) {
      width: 100px;
      height: 100px;
      top: 10%;
      right: 10%;
      animation-delay: 0s;
      background: linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 138, 80, 0.2));
    }

    &:nth-child(2) {
      width: 60px;
      height: 60px;
      bottom: 20%;
      left: 8%;
      animation-delay: -3s;
      background: linear-gradient(135deg, rgba(76, 175, 80, 0.12), rgba(102, 187, 106, 0.18));
    }

    &:nth-child(3) {
      width: 80px;
      height: 80px;
      top: 50%;
      right: 15%;
      animation-delay: -6s;
      background: linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(186, 104, 200, 0.15));
    }

    &:nth-child(4) {
      width: 70px;
      height: 70px;
      bottom: 10%;
      right: 25%;
      animation-delay: -9s;
      background: linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 213, 79, 0.18));
    }

    &:nth-child(5) {
      width: 50px;
      height: 50px;
      top: 25%;
      left: 15%;
      animation-delay: -12s;
      background: linear-gradient(135deg, rgba(0, 188, 212, 0.1), rgba(77, 208, 225, 0.16));
    }
  }

  @media (max-width: 768px) {
    .orb {
      &:nth-child(1) { width: 60px; height: 60px; }
      &:nth-child(2) { width: 40px; height: 40px; }
      &:nth-child(3) { width: 50px; height: 50px; }
      &:nth-child(4) { width: 45px; height: 45px; }
      &:nth-child(5) { width: 35px; height: 35px; }
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.2rem;
  margin-top: 1.5rem;
  position: relative;

  @media (max-width: 1024px) {
    gap: 1rem;
    margin-top: 1.2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
    margin-top: 1rem;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
    margin-top: 1rem;
    max-width: 280px;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.92)
  );
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  padding: 1.2rem 0.8rem;
  border-radius: 14px;
  text-align: center;
  box-shadow: 
    0 8px 25px rgba(0, 191, 255, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out both;

  &:nth-child(1) { animation-delay: 1s; }
  &:nth-child(2) { animation-delay: 1.2s; }
  &:nth-child(3) { animation-delay: 1.4s; }
  &:nth-child(4) { animation-delay: 1.6s; }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #00bfff, #1e90ff, #0066cc);
    transform: scaleX(0);
    transition: transform 0.4s ease;
    border-radius: 14px 14px 0 0;
  }

  &:hover {
    transform: translateY(-3px) scale(1.005);
    box-shadow: 
      0 12px 30px rgba(0, 191, 255, 0.12),
      0 6px 15px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.98)
    );

    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 768px) {
    padding: 0.9rem 0.6rem;
    border-radius: 12px;
    min-height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    
    &:hover {
      transform: translateY(-2px) scale(1.002);
    }
  }

  @media (max-width: 480px) {
    padding: 0.8rem 0.5rem;
    border-radius: 10px;
    min-height: 75px;
    
    &:hover {
      transform: translateY(-1px);
    }
  }
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  margin-bottom: 0.2rem;
  line-height: 1;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 0.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.15rem;
  }

  &.customers {
    color: #00bfff;
    text-shadow: 0 0 12px rgba(0, 191, 255, 0.2);
  }
  &.states {
    color: #1e90ff;
    text-shadow: 0 0 12px rgba(30, 144, 255, 0.2);
  }
  &.satisfaction {
    color: #ff6b35;
    text-shadow: 0 0 12px rgba(255, 107, 53, 0.2);
  }
  &.layers {
    color: #4caf50;
    text-shadow: 0 0 12px rgba(76, 175, 80, 0.2);
  }
`;

const StatLabel = styled.div`
  color: #4a5568;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 0.65rem;
    letter-spacing: 0.2px;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 0.6rem;
    letter-spacing: 0.1px;
    line-height: 1.1;
  }
`;

// Enhanced counter component
const AnimatedCounter = ({ end, suffix = "", duration = 2000, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay, isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const endValue = typeof end === "string" ? parseInt(end) : end;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOutCubic * endValue);

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  const displayValue = typeof end === "string" && end.includes("L")
    ? `${count}L${suffix}`
    : `${count}${suffix}`;

  return <span ref={ref}>{displayValue}</span>;
};

// Lazy Loading Image Component
const LazyLoadImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = () => {
    console.warn('Failed to load image:', src);
    setLoaded(true); // Still hide placeholder on error
  };

  return (
    <div ref={imgRef} style={{ position: 'relative', display: 'inline-block' }}>
      {!loaded && (
        <ImagePlaceholder>
          <LoadingSpinner />
        </ImagePlaceholder>
      )}
      {inView && (
        <LazyImage
          src={src}
          alt={alt}
          className={className}
          loaded={loaded}
          onLoad={handleLoad}
          onError={handleError}
          loading="eager"
          decoding="async"
          fetchpriority="high"
        />
      )}
    </div>
  );
};

const Home = () => {
  return (
    <HomeContainer>
      <FloatingOrbs>
        <div className="orb"></div>
        <div className="orb"></div>
        <div className="orb"></div>
        <div className="orb"></div>
        <div className="orb"></div>
      </FloatingOrbs>
      
      <HeroSection>
        <HeroContent>
          <TrustBadge>
            ⭐ Trusted by 50L+ Women Across India
          </TrustBadge>
          
          <LogoContainer>
            <img
              src={LogoImage}
              alt="ASKK International Logo"
              className="main-logo"
            />
          </LogoContainer>
          
          <MainHeading>
            Empowering Women's Health
          </MainHeading>
          
          <StatsContainer>
            <StatCard>
              <StatNumber className="customers">
                <AnimatedCounter end="50" suffix="L+" delay={200} />
              </StatNumber>
              <StatLabel>Happy Customers</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber className="states">
                <AnimatedCounter end="28" suffix="+" delay={400} />
              </StatNumber>
              <StatLabel>States Covered</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber className="satisfaction">
                <AnimatedCounter end="99" suffix="%" delay={600} />
              </StatNumber>
              <StatLabel>Satisfaction Rate</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber className="layers">
                <AnimatedCounter end="7" suffix=" Layer" delay={800} />
              </StatNumber>
              <StatLabel>Protection</StatLabel>
            </StatCard>
          </StatsContainer>
        </HeroContent>
        
        <HeroVisual>
          <LazyLoadImage
            src={AnchoringBg}
            alt="ASKK International - Empowering Women's Health"
            className="hero-image"
          />
        </HeroVisual>
      </HeroSection>
    </HomeContainer>
  );
};

export default Home;